import { AssetImageUploadField } from "@/components/asset-image-upload-field";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet";
import { Text } from "@/components/ui/Text";
import { useOrganization } from "@/contexts/Auth";
import { useRoutes } from "@/routes";
import { useCreateOrganizationRemoteSessionIssuerMutation } from "@gram/client/react-query/createOrganizationRemoteSessionIssuer.js";
import { useListProjects } from "@gram/client/react-query/listProjects.js";
import { invalidateAllOrganizationRemoteSessionIssuers } from "@gram/client/react-query/organizationRemoteSessionIssuers.js";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { ExistingIssuerLink } from "./ExistingIssuerLink";
import { Stack } from "@/components/ui/Stack";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  EndpointsFields,
  IssuerUrlField,
} from "../mcp/x/tabs/settings/sections/authentication/IssuerFormFields";
import {
  deriveNameFromUrl,
  deriveSlugFromUrl,
} from "../mcp/x/tabs/settings/sections/authentication/issuerFormUtils";
import { useIssuerDiscovery } from "../mcp/x/tabs/settings/sections/authentication/useIssuerDiscovery";
import { IssuerDuplicateWarning } from "../mcp/x/tabs/settings/sections/authentication/IssuerDuplicateWarning";
import { useIssuerDuplicatePreflight } from "../mcp/x/tabs/settings/sections/authentication/useIssuerDuplicatePreflight";
import { buildCreateIssuerForm } from "./issuerSettingsForm";

// Sentinel for the "no project" (organizational) selection. Radix Select treats
// the empty string specially, so we use an explicit value and map it back to an
// omitted projectId on submit.
const ORGANIZATIONAL = "organizational";

// CreateRemoteIdentityProviderSheet is a pared-down sibling of the MCP
// Authentication tab's AttachRemoteIdentityProviderSheet: it configures only the
// upstream issuer (URL, slug, display name, endpoints + RFC 8414 discovery) and
// creates a remote_session_issuer via the org-admin createIssuer endpoint.
// No project selection creates an organizational issuer (project_id NULL,
// inherited everywhere); selecting a project creates a project-specific one. It
// does not register a client or touch an MCP server — client management lives on
// the issuer detail page. On success it routes to the new issuer's detail page.
export function CreateRemoteIdentityProviderSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const organization = useOrganization();

  const { data: projectsData } = useListProjects({
    organizationId: organization.id,
  });
  const projects = useMemo(() => projectsData?.projects ?? [], [projectsData]);

  const [projectId, setProjectId] = useState<string>(ORGANIZATIONAL);
  const routes = useRoutes({
    projectSlug: projects.find((project) => project.id === projectId)?.slug,
  });

  // Display name + slug auto-derive from the Issuer URL hostname until the
  // operator edits them, after which the *Dirty flags lock in their value.
  const [name, setName] = useState("");
  const [nameDirty, setNameDirty] = useState(false);
  const [logoAssetId, setLogoAssetId] = useState("");
  // Create is held while a logo upload is in flight: submitting mid-upload
  // would persist the pre-upload value and silently drop the picked logo.
  const [logoUploading, setLogoUploading] = useState(false);
  const [slug, setSlug] = useState("");
  const [slugDirty, setSlugDirty] = useState(false);
  const [clientSetupDocumentationUrl, setClientSetupDocumentationUrl] =
    useState("");

  // The Issuer URL as it stood when the operator last left the field. Held
  // separately from the live input so the duplicate preflight runs on a settled
  // value rather than once per keystroke.
  const [settledIssuerUrl, setSettledIssuerUrl] = useState("");
  // Org scope: matches span the whole organization, project-specific records
  // included, plus the platform catalog. That is the point at this tier — an
  // administrator adding an organization-level provider most needs to know
  // which projects already configured the same URL on their own.
  const { matches: duplicateMatches } = useIssuerDuplicatePreflight({
    issuerUrl: settledIssuerUrl,
    scope: "organization",
    enabled: open,
  });

  const {
    issuerUrl,
    setIssuerUrl,
    authorizationEndpoint,
    setAuthorizationEndpoint,
    tokenEndpoint,
    setTokenEndpoint,
    registrationEndpoint,
    setRegistrationEndpoint,
    jwksUri,
    setJwksUri,
    discoveredSnapshot,
    discoverPending,
    discoverError,
    clearDiscoverError,
    runDiscover,
    handleResetEndpoints,
    resetEndpointState,
    showDiscoverControls,
    showResetControls,
    endpointWarnings,
    // This sheet can create an organization-level issuer, which has no project
    // to authorize against, so it fetches metadata through the org-scoped
    // endpoint rather than borrowing the active project's scope.
  } = useIssuerDiscovery(null, { scope: "organization" });

  const createMutation = useCreateOrganizationRemoteSessionIssuerMutation({
    onSuccess: async (created) => {
      await invalidateAllOrganizationRemoteSessionIssuers(queryClient, {
        refetchType: "all",
      });
      toast.success("Remote identity provider created");
      onOpenChange(false);
      routes.remoteIdentityProviders.issuerDetail.goTo(created.id);
    },
    onError: (error) => {
      // useMutation surfaces error.message via createMutation.error (shown in
      // the inline Alert); a console line keeps the stack for debugging.
      console.error("Create remote identity provider failed", error);
    },
  });

  const submitting = createMutation.isPending;
  const submitError = createMutation.error
    ? createMutation.error instanceof Error && createMutation.error.message
      ? createMutation.error.message
      : "An unexpected error occurred. Please try again."
    : null;
  const { reset: resetCreateMutation } = createMutation;

  // Reset transient state whenever the sheet is reopened so a prior draft never
  // leaks into a new creation.
  useEffect(() => {
    if (!open) return;
    setProjectId(ORGANIZATIONAL);
    setName("");
    setNameDirty(false);
    setLogoAssetId("");
    setSlug("");
    setSlugDirty(false);
    setClientSetupDocumentationUrl("");
    setIssuerUrl("");
    setSettledIssuerUrl("");
    resetEndpointState();
    clearDiscoverError();
    resetCreateMutation();
  }, [
    open,
    setIssuerUrl,
    resetEndpointState,
    clearDiscoverError,
    resetCreateMutation,
  ]);

  const submittable = useMemo(
    () => !!slug.trim() && !!issuerUrl.trim(),
    [slug, issuerUrl],
  );

  const handleSubmit = () => {
    if (!submittable || submitting || logoUploading) return;
    createMutation.mutate({
      request: {
        createIssuerRequestBody: {
          projectId: projectId === ORGANIZATIONAL ? undefined : projectId,
          ...buildCreateIssuerForm({
            name,
            logoAssetId,
            slug,
            clientSetupDocumentationUrl,
            issuerUrl,
            authorizationEndpoint,
            tokenEndpoint,
            registrationEndpoint,
            jwksUri,
            discoveredSnapshot,
          }),
        },
      },
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-[560px] flex-col sm:max-w-[560px]"
      >
        <SheetHeader className="px-6 pt-6 pb-0">
          <SheetTitle className="text-lg font-semibold">
            New Remote Identity Provider
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-6">
          <Stack gap={4}>
            <Stack gap={2}>
              <Label className="text-muted-foreground text-xs">Scope</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ORGANIZATIONAL}>
                    Organizational (all projects)
                  </SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Text muted small>
                Organizational providers are inherited by every project. Choose
                a project to scope the provider to it.
              </Text>
            </Stack>

            <IssuerUrlField
              issuerUrl={issuerUrl}
              onIssuerUrlSettled={setSettledIssuerUrl}
              duplicateWarning={
                <IssuerDuplicateWarning
                  viewerScope="organization"
                  matches={duplicateMatches}
                  renderLink={(match) => (
                    <ExistingIssuerLink
                      match={match}
                      onClick={() => onOpenChange(false)}
                    />
                  )}
                />
              }
              onIssuerUrlChange={(value) => {
                setIssuerUrl(value);
                // Any edit invalidates the last blur, so a warning cannot
                // outlive the URL it describes.
                setSettledIssuerUrl("");
                clearDiscoverError();
                if (!slugDirty) {
                  const derived = deriveSlugFromUrl(value);
                  if (derived) setSlug(derived);
                }
                if (!nameDirty) {
                  const derivedName = deriveNameFromUrl(value);
                  if (derivedName) setName(derivedName);
                }
                // When the URL diverges from a settled discovery the endpoints
                // are stale; reset them so the operator re-runs Discover.
                if (
                  discoveredSnapshot &&
                  value.trim() !== discoveredSnapshot.url
                ) {
                  resetEndpointState();
                }
              }}
            />

            <Stack gap={2}>
              <Label className="text-muted-foreground text-xs">Slug</Label>
              <Input
                value={slug}
                onChange={(value) => {
                  setSlug(value);
                  setSlugDirty(true);
                }}
                placeholder="my-identity-provider"
              />
              <Text muted small>
                Identifier for this identity provider. Auto-derived from the
                Issuer URL until you edit it.
              </Text>
            </Stack>

            <Stack gap={2}>
              <Label className="text-muted-foreground text-xs">
                Display name (optional)
              </Label>
              <Input
                value={name}
                onChange={(value) => {
                  setName(value);
                  setNameDirty(true);
                }}
                placeholder="My Identity Provider"
              />
              <Text muted small>
                Friendly label shown in the dashboard. Auto-derived from the
                Issuer URL until you edit it; falls back to the Issuer URL when
                left blank.
              </Text>
            </Stack>

            <AssetImageUploadField
              tier="organization"
              value={logoAssetId}
              onChange={setLogoAssetId}
              onUploadingChange={setLogoUploading}
              description="Shown beside this provider in the dashboard and on the connect consent page."
            />

            <Stack gap={2}>
              <Label className="text-muted-foreground text-xs">
                Client setup documentation URL (optional)
              </Label>
              <Input
                value={clientSetupDocumentationUrl}
                onChange={setClientSetupDocumentationUrl}
                placeholder="https://docs.example.com/oauth/apps"
              />
              <Text muted small>
                Linked from the New Client sheet so operators can set up an
                OAuth client with this provider themselves.
              </Text>
            </Stack>

            <EndpointsFields
              issuerUrl={issuerUrl}
              authorizationEndpoint={authorizationEndpoint}
              tokenEndpoint={tokenEndpoint}
              registrationEndpoint={registrationEndpoint}
              jwksUri={jwksUri}
              endpointWarnings={endpointWarnings}
              discoverPending={discoverPending}
              discoverError={discoverError}
              showDiscoverControls={showDiscoverControls}
              showResetControls={showResetControls}
              onAuthorizationEndpointChange={setAuthorizationEndpoint}
              onTokenEndpointChange={setTokenEndpoint}
              onRegistrationEndpointChange={setRegistrationEndpoint}
              onJwksUriChange={setJwksUri}
              onDiscover={() => {
                runDiscover(issuerUrl);
              }}
              onResetEndpoints={handleResetEndpoints}
            />
          </Stack>

          {submitError && (
            <Alert variant="error" dismissible={false}>
              {submitError}
            </Alert>
          )}
        </div>

        <SheetFooter className="flex-row items-center justify-end gap-2 border-t px-6 py-4">
          <Button
            variant="secondary"
            disabled={submitting}
            onClick={() => onOpenChange(false)}
          >
            <Button.Text>Cancel</Button.Text>
          </Button>
          <Button
            variant="primary"
            disabled={!submittable || submitting || logoUploading}
            onClick={handleSubmit}
          >
            <Button.Text>{submitting ? "Creating…" : "Create"}</Button.Text>
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
